/**
 * PwaUpdateService
 *
 * Purpose:
 *   Detects when a newer build of the app has been deployed and is ready to run,
 *   then prompts the user (via an Ionic toast) to reload into the new version.
 *   Also periodically checks for updates while the app is running and recovers
 *   from an unrecoverable service-worker state by forcing a reload.
 *
 * Responsibility:
 *   This is the single place that owns the PWA update lifecycle. Components and
 *   the rest of the app should not talk to SwUpdate directly; they just rely on
 *   this service being initialized once at startup.
 *
 * Notes:
 *   - Requires the Angular service worker to be enabled (see ServiceWorkerModule
 *     in app.module.ts). When it is disabled (e.g. local dev), this service no-ops.
 *   - For the toast to actually surface a new build, index.html and ngsw.json must
 *     NOT be long-cached by the CDN/browser, otherwise the new manifest is never
 *     fetched and no update is ever detected.
 *   - This app uses Ionic via NgModule (IonicModule.forRoot), so ToastController
 *     is imported from '@ionic/angular' (not the standalone entrypoint).
 */

import { Injectable, inject } from '@angular/core';
import {
  SwUpdate,
  VersionReadyEvent,
  UnrecoverableStateEvent,
} from '@angular/service-worker';
import { AlertController, ToastController } from '@ionic/angular';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  // #region Fields
  private readonly _swUpdate = inject(SwUpdate);
  private readonly _toastController = inject(ToastController);
  private readonly _alertController = inject(AlertController);

  /** How often to poll for a new build while the app is open (ms). */
  private readonly _checkIntervalMs = 60 * 1000;

  /**
   * Guards against showing more than one update prompt at a time. Set once the
   * first alert appears and left set through the "Hold on" reminder — the whole
   * chain is about a single pending build, so we never want to stack prompts.
   */
  private _promptOpen = false;
  // #endregion

  // #region Lifecycle / Init
  /**
   * Wires up update detection. Call once at app startup (e.g. from AppComponent).
   * Safe to call when the service worker is disabled; it simply does nothing.
   */
  public initialize(): void {
    if (!this._swUpdate.isEnabled) {
      return;
    }

    this.watchForVersionReady();
    this.watchForUnrecoverableState();
    this.schedulePeriodicChecks();
    this.watchForForeground();

    // Check once at startup so a build deployed while the app was closed is
    // picked up as soon as it opens, rather than only after the first interval.
    void this.checkForUpdate();
  }
  // #endregion

  // #region Update Detection
  /**
   * Listens for a newly downloaded build that is ready to activate and prompts
   * the user to reload into it.
   */
  private watchForVersionReady(): void {
    this._swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent =>
            event.type === 'VERSION_READY'
        )
      )
      .subscribe(() => {
        void this.promptToReload();
      });
  }

  /**
   * Listens for an unrecoverable service-worker state (cached files are missing
   * or corrupt). In this case the app cannot continue safely, so we tell the
   * user and force a full reload to fetch a clean copy.
   */
  private watchForUnrecoverableState(): void {
    this._swUpdate.unrecoverable.subscribe(
      (event: UnrecoverableStateEvent) => {
        console.error('PWA unrecoverable state:', event.reason);
        void this.promptToReload({
          header: 'Reload required',
          message: 'The app hit a problem and needs to reload to recover.',
          allowLater: false,
        });
      }
    );
  }

  /**
   * Polls for a new build on a fixed interval while the app is open.
   *
   * Note: we intentionally do NOT gate this on ApplicationRef.isStable. In an
   * installed PWA the app can stay "unstable" (background token timers, etc.),
   * which would mean the poll never starts. The service worker is registered
   * via 'registerWhenStable:30000', so it is ready well before the first tick.
   */
  private schedulePeriodicChecks(): void {
    interval(this._checkIntervalMs).subscribe(() => {
      void this.checkForUpdate();
    });
  }

  /**
   * Checks for a new build whenever the app returns to the foreground.
   *
   * This is the key path for an installed PWA: it is backgrounded/foregrounded
   * rather than reloaded, and the OS throttles timers while it is hidden — so
   * the interval poll effectively pauses. Re-checking on 'visible' means simply
   * reopening the app surfaces a build that was deployed while it was closed,
   * without the user needing a pull-to-refresh (which just re-renders the
   * service-worker-cached shell and does not force an update check).
   */
  private watchForForeground(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdate();
      }
    });
  }

  /**
   * Asks the service worker to check the server for a newer build. Any newly
   * found build surfaces through the versionUpdates stream (see
   * watchForVersionReady), which is what actually prompts the user.
   */
  private async checkForUpdate(): Promise<void> {
    try {
      await this._swUpdate.checkForUpdate();
    } catch (err) {
      console.error('PWA update check failed:', err);
    }
  }
  // #endregion

  // #region User Prompt
  /**
   * Announces a pending build with a prominent, backdrop-dimmed alert the user
   * has to engage with (no backdrop dismiss). "Reload now" activates the new
   * build immediately; "Hold on" steps down to a gentle, persistent bottom
   * toast (see showLaterReminder) so they can finish what they were doing and
   * reload when ready. On the unrecoverable path `allowLater` is false, so the
   * only way out is to reload.
   *
   * @param options.header    Alert title.
   * @param options.message   Alert body copy.
   * @param options.allowLater Whether to offer the "Hold on" (reload later) path.
   */
  private async promptToReload(options?: {
    header?: string;
    message?: string;
    allowLater?: boolean;
  }): Promise<void> {
    if (this._promptOpen) {
      return;
    }
    this._promptOpen = true;

    const header = options?.header ?? 'Update available';
    const message =
      options?.message ??
      'A new version of the app is ready. Reload to get the latest.';
    const allowLater = options?.allowLater ?? true;

    const buttons: any[] = [];
    if (allowLater) {
      // Left/secondary: defer without losing the prompt — hands off to the
      // gentle reminder toast so the user can keep working.
      buttons.push({
        text: 'Hold on',
        role: 'cancel',
        handler: () => {
          void this.showLaterReminder();
        },
      });
    }
    // Right/primary: reload straight into the new build.
    buttons.push({
      text: 'Reload now',
      handler: () => {
        void this.activateAndReload();
      },
    });

    const alert = await this._alertController.create({
      header,
      message,
      backdropDismiss: false, // Require an explicit choice.
      cssClass: 'pwa-update-alert',
      buttons,
    });

    await alert.present();
  }

  /**
   * The "Hold on" step-down: a persistent, non-blocking bottom toast that keeps
   * the update within reach without interrupting. It stays put (no timeout, no
   * dismiss) until the user taps "Reload now" — the single pending build is
   * always one tap away.
   */
  private async showLaterReminder(): Promise<void> {
    const toast = await this._toastController.create({
      message: 'Update ready when you are.',
      position: 'bottom',
      duration: 0, // Stay until the user reloads.
      buttons: [
        {
          text: 'Reload now',
          role: 'info',
          handler: () => {
            void this.activateAndReload();
          },
        },
      ],
    });

    await toast.present();
  }

  /**
   * Activates the waiting service-worker version and reloads the page so the
   * new build takes control. Falls back to a plain reload if activation fails.
   */
  private async activateAndReload(): Promise<void> {
    try {
      await this._swUpdate.activateUpdate();
    } catch (err) {
      console.error('PWA activateUpdate failed, reloading anyway:', err);
    } finally {
      document.location.reload();
    }
  }
  // #endregion
}
